import { environment } from "$lib/env"
import { DetectedCountry } from "$lib/stores"
import { browser } from "$app/environment"
import { get } from "svelte/store"

class GeolocationService {
  private apiUrl: string
  private countryDetected = false
  private detectionPromise: Promise<string | null> | null = null

  constructor() {
    this.apiUrl = environment.apiUrl
  }

  async detectCountry(): Promise<string | null> {
    // If we already detected the country, return from store
    const currentCountry = this.getCurrentCountry()
    if (this.countryDetected && currentCountry) {
      return currentCountry
    }

    // If detection is already in progress, return the existing promise
    if (this.detectionPromise) {
      return this.detectionPromise
    }

    // Start detection process and cache the promise
    this.detectionPromise = this.performDetection()

    try {
      const result = await this.detectionPromise
      return result
    } finally {
      // Clear the promise cache after completion (success or failure)
      this.detectionPromise = null
    }
  }

  private async performDetection(): Promise<string | null> {
    try {
      // First try to get country from backend
      const response = await fetch(`${this.apiUrl}/me`)
      const data = await response.json()

      if (data.countryCode && data.countryCode !== "NL") {
        console.log("Country detected from /me endpoint:", data.countryCode)
        DetectedCountry.set(data.countryCode)
        this.countryDetected = true
        return data.countryCode
      }

      // If backend doesn't provide country or returns NL (server location), try browser geolocation API
      if (browser) {
        try {
          // Use a third-party geolocation API as fallback
          const geoResponse = await fetch("https://ipapi.co/json/")
          const geoData = await geoResponse.json()

          if (geoData.country_code) {
            console.log("Country detected from ipapi.co:", geoData.country_code)
            DetectedCountry.set(geoData.country_code)
            this.countryDetected = true
            return geoData.country_code
          }
        } catch (error) {
          console.error("Error with ipapi.co:", error)

          // Try another service as backup
          try {
            const geoResponse2 = await fetch("https://ipinfo.io/json")
            const geoData2 = await geoResponse2.json()

            if (geoData2.country) {
              console.log("Country detected from ipinfo.io:", geoData2.country)
              DetectedCountry.set(geoData2.country)
              this.countryDetected = true
              return geoData2.country
            }
          } catch (error2) {
            console.error("Error with ipinfo.io:", error2)
          }
        }
      }

      return null
    } catch (error) {
      console.error("Error detecting country:", error)
      return null
    }
  }

  getCurrentCountry(): string | null {
    // Use get() to access the store value without creating a subscription
    return get(DetectedCountry)
  }

  // Helper method to check if user is from a specific country
  isCountry(countryCode: string): boolean {
    const currentCountry = this.getCurrentCountry()
    return currentCountry === countryCode
  }

  // Helper method to check if user is from any of the specified countries
  isAnyCountry(countryCodes: string[]): boolean {
    const currentCountry = this.getCurrentCountry()
    return currentCountry !== null && countryCodes.includes(currentCountry)
  }
}

export const geolocationService = new GeolocationService()